/**
 * WeeklySummaryPage Component
 *
 * Shows weekly overview with key metrics and daily entry counts.
 * Allows navigation between weeks and quick access to daily views.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../lib/api';
import { getLocalDateString, getWeekStart, getWeekEnd } from '../lib/dateUtils';

interface DailyCount {
  date: string;
  meals: number;
  exercise: number;
  weight: number;
  health: number;
  medical: number;
  activities: number;
}

interface WeekData {
  startDate: string;
  endDate: string;
  days: DailyCount[];
  weightStart: number | null;
  weightEnd: number | null;
  totalMeals: number;
  totalExercise: number;
}

/**
 * Format date for display
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
}

/**
 * Get day name in Indonesian
 */
function getDayName(dateStr: string): string {
  const date = new Date(dateStr);
  const days = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
  return days[date.getDay()];
}

export function WeeklySummaryPage() {
  const navigate = useNavigate();
  const [selectedWeekStart, setSelectedWeekStart] = useState(
    getWeekStart(getLocalDateString())
  );
  const [weekData, setWeekData] = useState<WeekData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWeekData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const startDate = selectedWeekStart;
    const endDate = getWeekEnd(selectedWeekStart);

    try {
      // Fetch all entry types for the week
      const [weightRes, mealsRes, exerciseRes, healthRes, visitsRes, activitiesRes] =
        await Promise.all([
          fetch(apiUrl(`/api/weight?startDate=${startDate}&endDate=${endDate}`), {
            credentials: 'include',
          }),
          fetch(apiUrl(`/api/meals?startDate=${startDate}&endDate=${endDate}`), {
            credentials: 'include',
          }),
          fetch(apiUrl(`/api/exercise?startDate=${startDate}&endDate=${endDate}`), {
            credentials: 'include',
          }),
          fetch(apiUrl(`/api/health-metrics?startDate=${startDate}&endDate=${endDate}`), {
            credentials: 'include',
          }),
          fetch(apiUrl(`/api/medical/visits?startDate=${startDate}&endDate=${endDate}`), {
            credentials: 'include',
          }),
          fetch(apiUrl(`/api/activities?startDate=${startDate}&endDate=${endDate}`), {
            credentials: 'include',
          }),
        ]);

      const [weightData, mealsData, exerciseData, healthData, visitsData, activitiesData] =
        await Promise.all([
          weightRes.ok ? weightRes.json() : { entries: [] },
          mealsRes.ok ? mealsRes.json() : { entries: [] },
          exerciseRes.ok ? exerciseRes.json() : { entries: [] },
          healthRes.ok ? healthRes.json() : { entries: [] },
          visitsRes.ok ? visitsRes.json() : { entries: [] },
          activitiesRes.ok ? activitiesRes.json() : { entries: [] },
        ]);

      // Build daily counts
      const days: DailyCount[] = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate + 'T00:00:00');
        date.setDate(date.getDate() + i);
        const dateStr = getLocalDateString(date);

        days.push({
          date: dateStr,
          meals: (mealsData.entries || []).filter(
            (e: { date: string }) => e.date.split('T')[0] === dateStr
          ).length,
          exercise: (exerciseData.entries || []).filter(
            (e: { date: string }) => e.date.split('T')[0] === dateStr
          ).length,
          weight: (weightData.entries || []).filter(
            (e: { date: string }) => e.date.split('T')[0] === dateStr
          ).length,
          health: (healthData.entries || []).filter(
            (e: { date: string }) => e.date.split('T')[0] === dateStr
          ).length,
          medical: (visitsData.entries || []).filter(
            (e: { date: string }) => e.date.split('T')[0] === dateStr
          ).length,
          activities: (activitiesData.entries || []).filter(
            (e: { date: string }) => e.date.split('T')[0] === dateStr
          ).length,
        });
      }

      // Get weight trend
      const weights = (weightData.entries || [])
        .map((e: { date: string; weight: string }) => ({
          date: e.date,
          weight: parseFloat(e.weight),
        }))
        .sort(
          (a: { date: string }, b: { date: string }) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        );

      setWeekData({
        startDate,
        endDate,
        days,
        weightStart: weights.length > 0 ? weights[0].weight : null,
        weightEnd: weights.length > 0 ? weights[weights.length - 1].weight : null,
        totalMeals: (mealsData.entries || []).length,
        totalExercise: (exerciseData.entries || []).length,
      });
    } catch (err) {
      console.error('Failed to fetch week data:', err);
      setError('Gagal memuat data. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  }, [selectedWeekStart]);

  useEffect(() => {
    fetchWeekData();
  }, [fetchWeekData]);

  const goToPreviousWeek = () => {
    const prev = new Date(selectedWeekStart + 'T00:00:00');
    prev.setDate(prev.getDate() - 7);
    setSelectedWeekStart(getLocalDateString(prev));
  };

  const goToNextWeek = () => {
    const next = new Date(selectedWeekStart + 'T00:00:00');
    next.setDate(next.getDate() + 7);
    const today = getLocalDateString();
    if (getLocalDateString(next) <= getWeekStart(today)) {
      setSelectedWeekStart(getLocalDateString(next));
    }
  };

  const goToCurrentWeek = () => {
    setSelectedWeekStart(getWeekStart(getLocalDateString()));
  };

  const goToDay = (dateStr: string) => {
    navigate(`/entries?date=${dateStr}`);
  };

  const isCurrentWeek =
    selectedWeekStart === getWeekStart(getLocalDateString());

  // Calculate weight change
  const weightChange =
    weekData?.weightStart && weekData?.weightEnd
      ? weekData.weightEnd - weekData.weightStart
      : null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Ringkasan Mingguan</h1>
        <p className="text-gray-600 mt-1">
          Pantau perkembangan kesehatan minggu ini
        </p>
      </div>

      {/* Week Navigation */}
      <div className="bg-white shadow rounded-lg p-4 mb-4">
        <div className="flex items-center gap-4">
          <button
            onClick={goToPreviousWeek}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            aria-label="Minggu sebelumnya"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          <div className="flex-1 text-center">
            <span className="font-medium">
              {formatDate(selectedWeekStart)} - {formatDate(getWeekEnd(selectedWeekStart))}
            </span>
          </div>

          <button
            onClick={goToNextWeek}
            disabled={isCurrentWeek}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Minggu berikutnya"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {!isCurrentWeek && (
            <button
              onClick={goToCurrentWeek}
              className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
            >
              Minggu Ini
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
          <button
            onClick={fetchWeekData}
            className="ml-2 text-red-800 underline hover:no-underline"
          >
            Coba lagi
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Week Data */}
      {!loading && weekData && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {/* Weight */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Berat</div>
              {weekData.weightEnd ? (
                <div>
                  <span className="text-2xl font-bold">{weekData.weightEnd.toFixed(2)}</span>
                  <span className="text-sm text-gray-500"> kg</span>
                  {weightChange !== null && weightChange !== 0 && (
                    <div
                      className={`text-sm ${
                        weightChange < 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {weightChange > 0 ? '+' : ''}
                      {weightChange.toFixed(2)} kg
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </div>

            {/* Meals */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Makanan</div>
              <div>
                <span className="text-2xl font-bold">{weekData.totalMeals}</span>
                <span className="text-sm text-gray-500"> catatan</span>
              </div>
            </div>

            {/* Exercise */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Olahraga</div>
              <div>
                <span className="text-2xl font-bold">{weekData.totalExercise}</span>
                <span className="text-sm text-gray-500"> sesi</span>
              </div>
            </div>

            {/* Completion */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Hari Aktif</div>
              <div>
                <span className="text-2xl font-bold">
                  {weekData.days.filter(
                    (d) => d.meals > 0 || d.exercise > 0 || d.weight > 0 || d.health > 0 || d.activities > 0
                  ).length}
                </span>
                <span className="text-sm text-gray-500">/7 hari</span>
              </div>
            </div>
          </div>

          {/* Daily Breakdown */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Catatan Harian</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {weekData.days.map((day) => {
                const today = getLocalDateString();
                const isToday = day.date === today;
                const isFuture = day.date > today;
                const hasEntries =
                  day.meals > 0 ||
                  day.exercise > 0 ||
                  day.weight > 0 ||
                  day.health > 0 ||
                  day.medical > 0 ||
                  day.activities > 0;

                return (
                  <button
                    key={day.date}
                    onClick={() => !isFuture && goToDay(day.date)}
                    disabled={isFuture}
                    className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition ${
                      isFuture ? 'opacity-50 cursor-not-allowed' : ''
                    } ${isToday ? 'bg-blue-50' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 text-center">
                        <div className="text-xs text-gray-500">{getDayName(day.date)}</div>
                        <div className={`font-semibold ${isToday ? 'text-blue-600' : ''}`}>
                          {new Date(day.date).getDate()}
                        </div>
                      </div>
                      {hasEntries ? (
                        <div className="flex gap-2 text-xs">
                          {day.meals > 0 && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                              M:{day.meals}
                            </span>
                          )}
                          {day.exercise > 0 && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">
                              O:{day.exercise}
                            </span>
                          )}
                          {day.weight > 0 && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                              B:{day.weight}
                            </span>
                          )}
                          {day.health > 0 && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                              K:{day.health}
                            </span>
                          )}
                          {day.medical > 0 && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">
                              D:{day.medical}
                            </span>
                          )}
                          {day.activities > 0 && (
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                              A:{day.activities}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">
                          {isFuture ? '-' : 'Tidak ada catatan'}
                        </span>
                      )}
                    </div>
                    {!isFuture && (
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
